import { configureStore, ThunkAction, Action, combineReducers } from '@reduxjs/toolkit';
import AuthentificationSlice from '../features/Authentification/authentificationSlice';
import AccountSlice, { resetAccount } from '../features/Accounts/accountSlice';
import DirectorySlice from '../features/Directories/directorySlice';
import StripeSlice from '../features/stripe/stripeSlice';
import LicensesSlice from '../features/Licenses/licensesSlice';
import { createTransform, PersistConfig, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import BlogSlice from '../features/Blogs/BlogSlice';
import ContactEmailSlice from '../features/ContactEmail/ContactEmailSlice';
import ProductSlice from '../features/Product/productSlice';
import PortalSlice from '../features/Portal/PortalSlice';
import { isProtectedRoute, setSessionExpiredHandler } from './authFetch';

// Request status is per-session, never per-browser. Persisting it means a spinner that was
// on screen when the tab closed comes back as a permanently stuck spinner, and a 401 from a
// previous session comes back as a redirect on a page that never made an authenticated call.
// Reset it in both directions so new writes are clean and already-poisoned storage self-heals
// on read, instead of bumping the persist version (which would purge everyone's login state).
export function sanitizeTransientState(value: any): any {
  if (Array.isArray(value)) {
    return value.map(sanitizeTransientState);
  }
  if (value === null || typeof value !== 'object' || Object.getPrototypeOf(value) !== Object.prototype) {
    return value;
  }
  const sanitized: Record<string, any> = {};
  Object.keys(value).forEach((key) => {
    const child = value[key];
    if (key === 'isLoading' && typeof child === 'boolean') {
      sanitized[key] = false;
    }
    else if ((key === 'statusCode' || key === 'error') && typeof child === 'string') {
      sanitized[key] = '';
    }
    else {
      sanitized[key] = sanitizeTransientState(child);
    }
  });
  return sanitized;
}

const reducer = combineReducers({
  AuthentificationSlice: AuthentificationSlice,
  AccountSlice: AccountSlice,
  DirectorySlice: DirectorySlice,
  LicensesSlice: LicensesSlice,
  StripeSlice: StripeSlice,
  BlogsSlice: BlogSlice,
  ContactSlice: ContactEmailSlice,
  ProductSlice: ProductSlice,
  PortalSlice: PortalSlice,
})

type RootReducerState = ReturnType<typeof reducer>;

const transientStateTransform = createTransform<any, any, RootReducerState, RootReducerState>(
  (inboundState) => sanitizeTransientState(inboundState),
  (outboundState) => sanitizeTransientState(outboundState)
);

// Pinned to RootReducerState so `transforms` cannot widen the config's state type and
// collapse RootState to any for every consumer of useSelector.
const persistConfig: PersistConfig<RootReducerState> = {
  key: 'root',
  version: 1,
  storage,
  // ProductSlice holds the downloaded installer as a Blob, which JSON-serializes to {} and
  // then reads back as a truthy-but-broken blob on the next visit.
  blacklist: ['LicensesSlice', 'DirectorySlice', 'AuthentificationSlice', 'BlogsSlice', 'ProductSlice'],
  transforms: [transientStateTransform]
};

const persistedReducer = persistReducer(persistConfig, reducer);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: false
});

// authFetch refreshes on a 401 and retries, so it only calls back when the refresh itself
// failed -- meaning the session is genuinely over rather than merely stale.
setSessionExpiredHandler(() => {
  store.dispatch(resetAccount());
  if (isProtectedRoute(window.location.pathname)) {
    window.location.replace('/login');
  }
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
